import type { ReactNode } from 'react';
import { useUsers } from '@/hooks/queries';
import { StatLine } from '@/components/ui';
import { formatBRLShort, formatNumber, formatRange, LEVERAGE_LONG_LABELS, PERIOD_LABELS, todayISO } from '@/lib/format';
import type { LeadDepositType, PeriodKey } from '@/lib/types';
import './ranking.css';

const PERIODS: PeriodKey[] = ['today', 'yesterday', 'week', 'month', 'lastMonth', 'quarter', 'year', 'all'];

/** Sem tipo, o depositado da corretora — o mesmo "Depositado" do ranking de membros. A UNA vem à parte. */
const TYPES: { value: LeadDepositType | ''; label: string }[] = [
  { value: '', label: 'Depositado (depósitos e alavancagens)' },
  { value: 'WELCOME', label: LEVERAGE_LONG_LABELS.WELCOME! },
  { value: 'EVENT', label: LEVERAGE_LONG_LABELS.EVENT! },
  { value: 'DEPOSIT', label: LEVERAGE_LONG_LABELS.DEPOSIT! },
  { value: 'UNA_DEPOSIT', label: LEVERAGE_LONG_LABELS.UNA_DEPOSIT! },
];

export type DepositView = 'board' | 'table';

/** O recorte do ranking de depósitos, o mesmo nas duas visões: trocar de visão não perde o período. */
export interface DepositFilters {
  period: PeriodKey;
  from: string;
  to: string;
  type: LeadDepositType | '';
  /** O membro creditado nos depósitos — só no ranking de clientes. */
  memberId: string;
  view: DepositView;
}

export const initialDepositFilters = (): DepositFilters => ({
  period: 'month',
  from: `${todayISO().slice(0, 8)}01`,
  to: todayISO(),
  type: '',
  memberId: '',
  view: 'board',
});

/** O recorte na API. Datas digitadas ao contrário viram o intervalo certo: a API recusaria e o erro tomaria a página. */
export function depositQuery({ period, from, to, type }: DepositFilters) {
  const [start, end] = from && to && from > to ? [to, from] : [from, to];
  return {
    period,
    ...(period === 'custom' ? { from: start || undefined, to: end || undefined } : {}),
    type: type || undefined,
  };
}

export const depositsLabel = (count: number) => `${formatNumber(count)} ${count === 1 ? 'depósito' : 'depósitos'}`;
export const clientsLabel = (count: number) => `${formatNumber(count)} ${count === 1 ? 'cliente' : 'clientes'}`;
export const typeSuffix = (type: LeadDepositType | null) => (type ? ` · ${LEVERAGE_LONG_LABELS[type]}` : '');
export const averagePerDeposit = (total: number, count: number) => (count > 0 ? Math.round(total / count) : 0);

interface ToolbarProps {
  filters: DepositFilters;
  onChange: (patch: Partial<DepositFilters>) => void;
  /** O filtro de membro: só no ranking de clientes (no de membros, cada um já é uma linha). */
  withMember?: boolean;
  /** Ações antes da troca Ranking/Tabela (ex.: Baixar CSV). */
  actions?: ReactNode;
}

/** Os filtros na mesma linha do ranking de membros, com a troca Ranking/Tabela no fim. */
export function DepositToolbar({ filters, onChange, withMember, actions }: ToolbarProps) {
  // Com os inativos: o ranking por membro mostra o inativo que depositou, e abrir os clientes dele filtra por ele.
  const { data: members } = useUsers({ includeInactive: 'true' }, { enabled: Boolean(withMember) });
  const custom = filters.period === 'custom';
  return (
    <div className="toolbar">
      <select className="inp" value={filters.period} onChange={(event) => onChange({ period: event.target.value as PeriodKey })} aria-label="Período">
        {PERIODS.map((option) => <option key={option} value={option}>{PERIOD_LABELS[option]}</option>)}
        <option value="custom">Personalizado</option>
      </select>

      {custom ? (
        <>
          <input className="inp" style={{ width: 150 }} type="date" value={filters.from} onChange={(event) => onChange({ from: event.target.value })} aria-label="Data inicial" />
          <span className="muted" style={{ fontSize: 12 }}>até</span>
          <input className="inp" style={{ width: 150 }} type="date" value={filters.to} onChange={(event) => onChange({ to: event.target.value })} aria-label="Data final" />
        </>
      ) : null}

      <select className="inp" value={filters.type} onChange={(event) => onChange({ type: event.target.value as LeadDepositType | '' })} aria-label="Tipo de depósito">
        {TYPES.map((option) => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
      </select>

      {withMember ? (
        // O membro creditado no comprovante — o mesmo filtro de membro das outras telas.
        <select className="inp" value={filters.memberId} onChange={(event) => onChange({ memberId: event.target.value })} aria-label="Membro">
          <option value="">Todos os membros</option>
          {members?.map((member) => (
            <option key={member.id} value={member.id}>{member.active ? member.name : `${member.name} (inativo)`}</option>
          ))}
        </select>
      ) : null}

      <div className="toolbar-end">
        {actions}
        <div className="view-switch" role="group" aria-label="Visão">
          <button type="button" aria-pressed={filters.view === 'board'} onClick={() => onChange({ view: 'board' })}>Ranking</button>
          <button type="button" aria-pressed={filters.view === 'table'} onClick={() => onChange({ view: 'table' })}>Tabela</button>
        </div>
      </div>
    </div>
  );
}

/** O período da comparação por extenso, para o "i"; null quando não há (período aberto). */
export const previousPeriodLabel = (data: { previousPeriod: { from: string; to: string } | null }) =>
  data.previousPeriod ? formatRange(data.previousPeriod.from, data.previousPeriod.to) : null;

/** O depósito sem cliente não tem onde entrar no ranking de clientes: aparece aqui, para a conta do leilão fechar. */
export function UnlinkedStat({ total, count }: { total: number; count: number }) {
  return (
    <StatLine
      label="Sem cliente vinculado"
      value={formatBRLShort(total)}
      color={count > 0 ? 'var(--warning)' : undefined}
      sub={count > 0 ? `${depositsLabel(count)} fora do ranking de clientes` : 'nenhum'}
    />
  );
}

export function NoDeposits({ label, period, onPeriod }: { label: string; period: PeriodKey; onPeriod: (period: PeriodKey) => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '34px 20px' }}>
      <div className="muted" style={{ fontSize: 13 }}>
        Nenhum depósito em <strong>{label.toLowerCase()}</strong> com esses filtros.
      </div>
      {period !== 'all' && period !== 'year' ? (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
          <button type="button" className="btn btn-sm" onClick={() => onPeriod('year')}>Ver o ano inteiro</button>
          <button type="button" className="btn btn-sm" onClick={() => onPeriod('all')}>Ver todo o período</button>
        </div>
      ) : null}
    </div>
  );
}
